/** Sentinel code for companies/entries whose country is unknown or missing. */
export const UNKNOWN_COUNTRY_CODE = "XX";

/**
 * Normalize a raw country code to a stable, upper-cased key, collapsing empty /
 * missing values to the {@link UNKNOWN_COUNTRY_CODE} sentinel.
 */
export const normalizeCountryCode = (countryCode?: string): string => {
  const normalized = countryCode?.trim().toUpperCase();
  return normalized?.length ? normalized : UNKNOWN_COUNTRY_CODE;
};

/** Translation function type for i18n (matches vue-i18n's t function signature). */
export type TranslationFn = (key: string, ...args: unknown[]) => string;

/** Key-existence check type (matches vue-i18n's te function signature). */
export type TranslationExistsFn = (key: string) => boolean;

// Mapping of ISO alpha-2 codes to common English names for all countries
// We use this instead of the heavy world-countries library
export const COUNTRY_NAMES: Record<string, string> = {
  AD: "Andorra",
  AE: "United Arab Emirates",
  AF: "Afghanistan",
  AG: "Antigua and Barbuda",
  AI: "Anguilla",
  AL: "Albania",
  AM: "Armenia",
  AO: "Angola",
  AQ: "Antarctica",
  AR: "Argentina",
  AS: "American Samoa",
  AT: "Austria",
  AU: "Australia",
  AW: "Aruba",
  AX: "Åland Islands",
  AZ: "Azerbaijan",
  BA: "Bosnia and Herzegovina",
  BB: "Barbados",
  BD: "Bangladesh",
  BE: "Belgium",
  BF: "Burkina Faso",
  BG: "Bulgaria",
  BH: "Bahrain",
  BI: "Burundi",
  BJ: "Benin",
  BL: "Saint Barthélemy",
  BM: "Bermuda",
  BN: "Brunei",
  BO: "Bolivia",
  BQ: "Caribbean Netherlands",
  BR: "Brazil",
  BS: "Bahamas",
  BT: "Bhutan",
  BV: "Bouvet Island",
  BW: "Botswana",
  BY: "Belarus",
  BZ: "Belize",
  CA: "Canada",
  CC: "Cocos (Keeling) Islands",
  CD: "DR Congo",
  CF: "Central African Republic",
  CG: "Republic of the Congo",
  CH: "Switzerland",
  CI: "Ivory Coast",
  CK: "Cook Islands",
  CL: "Chile",
  CM: "Cameroon",
  CN: "China",
  CO: "Colombia",
  CR: "Costa Rica",
  CU: "Cuba",
  CV: "Cape Verde",
  CW: "Curaçao",
  CX: "Christmas Island",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DJ: "Djibouti",
  DK: "Denmark",
  DM: "Dominica",
  DO: "Dominican Republic",
  DZ: "Algeria",
  EC: "Ecuador",
  EE: "Estonia",
  EG: "Egypt",
  EH: "Western Sahara",
  ER: "Eritrea",
  ES: "Spain",
  ET: "Ethiopia",
  FI: "Finland",
  FJ: "Fiji",
  FK: "Falkland Islands",
  FM: "Micronesia",
  FO: "Faroe Islands",
  FR: "France",
  GA: "Gabon",
  GB: "United Kingdom",
  GD: "Grenada",
  GE: "Georgia",
  GF: "French Guiana",
  GG: "Guernsey",
  GH: "Ghana",
  GI: "Gibraltar",
  GL: "Greenland",
  GM: "Gambia",
  GN: "Guinea",
  GP: "Guadeloupe",
  GQ: "Equatorial Guinea",
  GR: "Greece",
  GS: "South Georgia",
  GT: "Guatemala",
  GU: "Guam",
  GW: "Guinea-Bissau",
  GY: "Guyana",
  HK: "Hong Kong",
  HM: "Heard Island and McDonald Islands",
  HN: "Honduras",
  HR: "Croatia",
  HT: "Haiti",
  HU: "Hungary",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IM: "Isle of Man",
  IN: "India",
  IO: "British Indian Ocean Territory",
  IQ: "Iraq",
  IR: "Iran",
  IS: "Iceland",
  IT: "Italy",
  JE: "Jersey",
  JM: "Jamaica",
  JO: "Jordan",
  JP: "Japan",
  KE: "Kenya",
  KG: "Kyrgyzstan",
  KH: "Cambodia",
  KI: "Kiribati",
  KM: "Comoros",
  KN: "Saint Kitts and Nevis",
  KP: "North Korea",
  KR: "South Korea",
  KW: "Kuwait",
  KY: "Cayman Islands",
  KZ: "Kazakhstan",
  LA: "Laos",
  LB: "Lebanon",
  LC: "Saint Lucia",
  LI: "Liechtenstein",
  LK: "Sri Lanka",
  LR: "Liberia",
  LS: "Lesotho",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  LY: "Libya",
  MA: "Morocco",
  MC: "Monaco",
  MD: "Moldova",
  ME: "Montenegro",
  MF: "Saint Martin",
  MG: "Madagascar",
  MH: "Marshall Islands",
  MK: "North Macedonia",
  ML: "Mali",
  MM: "Myanmar",
  MN: "Mongolia",
  MO: "Macau",
  MP: "Northern Mariana Islands",
  MQ: "Martinique",
  MR: "Mauritania",
  MS: "Montserrat",
  MT: "Malta",
  MU: "Mauritius",
  MV: "Maldives",
  MW: "Malawi",
  MX: "Mexico",
  MY: "Malaysia",
  MZ: "Mozambique",
  NA: "Namibia",
  NC: "New Caledonia",
  NE: "Niger",
  NF: "Norfolk Island",
  NG: "Nigeria",
  NI: "Nicaragua",
  NL: "Netherlands",
  NO: "Norway",
  NP: "Nepal",
  NR: "Nauru",
  NU: "Niue",
  NZ: "New Zealand",
  OM: "Oman",
  PA: "Panama",
  PE: "Peru",
  PF: "French Polynesia",
  PG: "Papua New Guinea",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PM: "Saint Pierre and Miquelon",
  PN: "Pitcairn Islands",
  PR: "Puerto Rico",
  PS: "Palestine",
  PT: "Portugal",
  PW: "Palau",
  PY: "Paraguay",
  QA: "Qatar",
  RE: "Réunion",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  RW: "Rwanda",
  SA: "Saudi Arabia",
  SB: "Solomon Islands",
  SC: "Seychelles",
  SD: "Sudan",
  SE: "Sweden",
  SG: "Singapore",
  SH: "Saint Helena, Ascension and Tristan da Cunha",
  SI: "Slovenia",
  SJ: "Svalbard and Jan Mayen",
  SK: "Slovakia",
  SL: "Sierra Leone",
  SM: "San Marino",
  SN: "Senegal",
  SO: "Somalia",
  SR: "Suriname",
  SS: "South Sudan",
  ST: "São Tomé and Príncipe",
  SV: "El Salvador",
  SX: "Sint Maarten",
  SY: "Syria",
  SZ: "Eswatini",
  TC: "Turks and Caicos Islands",
  TD: "Chad",
  TF: "French Southern and Antarctic Lands",
  TG: "Togo",
  TH: "Thailand",
  TJ: "Tajikistan",
  TK: "Tokelau",
  TL: "Timor-Leste",
  TM: "Turkmenistan",
  TN: "Tunisia",
  TO: "Tonga",
  TR: "Turkey",
  TT: "Trinidad and Tobago",
  TV: "Tuvalu",
  TW: "Taiwan",
  TZ: "Tanzania",
  UA: "Ukraine",
  UG: "Uganda",
  UM: "United States Minor Outlying Islands",
  US: "United States",
  UY: "Uruguay",
  UZ: "Uzbekistan",
  VA: "Vatican City",
  VC: "Saint Vincent and the Grenadines",
  VE: "Venezuela",
  VG: "British Virgin Islands",
  VI: "United States Virgin Islands",
  VN: "Vietnam",
  VU: "Vanuatu",
  WF: "Wallis and Futuna",
  WS: "Samoa",
  XK: "Kosovo",
  YE: "Yemen",
  YT: "Mayotte",
  ZA: "South Africa",
  ZM: "Zambia",
  ZW: "Zimbabwe",
};

// Generate ALL_COUNTRIES from our lightweight map
export const ALL_COUNTRIES = Object.entries(COUNTRY_NAMES)
  .map(([code, name]) => ({
    value: code,
    label: name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label))
  .concat([{ value: UNKNOWN_COUNTRY_CODE, label: "Other" }]); // Add "Other" option at the end

/**
 * Translate an ISO country/registry code via i18n, falling back to the code itself.
 * Handles the unknown sentinel ("XX") by returning the "other" label.
 *
 * Registry codes like "BE-BRU" are normalized to "BE_BRU" to match the JSON key shape.
 * `te` is optional for ergonomic callers that only have `t`; when omitted we fall
 * back to vue-i18n's convention of returning the key on a missing entry.
 */
export const translateCountryCode = (
  countryCode: string,
  t: TranslationFn,
  te?: TranslationExistsFn
): string => {
  const normalizedCode = countryCode.replace(/-/g, "_");
  const key =
    normalizedCode === UNKNOWN_COUNTRY_CODE
      ? "common.country.other"
      : `common.country.${normalizedCode}`;

  const exists = te ? te(key) : t(key) !== key;
  return exists ? t(key) : countryCode;
};

/** A set of companies (or any country-bearing entity) bucketed under one country. */
export interface CountryGroup<T> {
  /** Normalized ISO country code, or {@link UNKNOWN_COUNTRY_CODE}. */
  code: string;
  /** Localized country name for display. */
  label: string;
  companies: T[];
}

/**
 * Group companies by their (normalized) country code, ordered by the localized
 * country label so groups read naturally in the active locale. The unknown
 * bucket always sinks to the bottom.
 */
export const groupCompaniesByCountry = <T extends { country?: string }>(
  companies: T[],
  getLabel: (code: string) => string,
  locale?: string
): CountryGroup<T>[] => {
  const groups = new Map<string, CountryGroup<T>>();

  for (const company of companies) {
    const code = normalizeCountryCode(company.country);
    const group = groups.get(code);
    if (group) {
      group.companies.push(company);
    } else {
      groups.set(code, { code, label: getLabel(code), companies: [company] });
    }
  }

  return [...groups.values()].sort((a, b) => {
    if (a.code === UNKNOWN_COUNTRY_CODE) return 1;
    if (b.code === UNKNOWN_COUNTRY_CODE) return -1;
    return a.label.localeCompare(b.label, locale);
  });
};

/**
 * Convert ISO2 country code to flag emoji
 * @returns flag emoji for valid country codes, empty string for invalid country codes
 */
export const getFlagEmoji = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return "";

  const upperCode = countryCode.toUpperCase();

  // Only generate flag emoji for valid country codes
  if (!COUNTRY_NAMES[upperCode]) return "";

  const codePoints = upperCode
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  try {
    return String.fromCodePoint(...codePoints);
  } catch {
    return "";
  }
};

/**
 * Human-readable country display: flag emoji + English name (e.g. "🇮🇹 Italy").
 * Falls back to the bare code when the country is unknown.
 */
export const formatCountryWithFlag = (countryCode: string): string => {
  const upperCode = countryCode.toUpperCase();
  const flag = getFlagEmoji(upperCode);
  const name = COUNTRY_NAMES[upperCode] ?? countryCode;
  return `${flag} ${name}`.trim();
};
